(function(global) {
    'use strict';

    function safeJsonParse(value, fallbackValue) {
        if (!value) return fallbackValue;
        try {
            let parsed = JSON.parse(value);
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            return parsed;
        } catch (error) {
            return fallbackValue;
        }
    }

    function parseUpholsteryConfigList(rawValue) {
        const parsed = safeJsonParse(rawValue, []);
        return Array.isArray(parsed) ? parsed : [];
    }

    function normalizeString(value) {
        return (value || '').toString().trim();
    }

    function normalizeLower(value) {
        return normalizeString(value).toLowerCase();
    }

    function normalizeOptionKey(value) {
        return normalizeLower(value).replace(/[^a-z0-9]/g, '');
    }

    function unwrapMetaobjectValue(value) {
        let current = value;
        let depth = 0;

        while (current && typeof current === 'object' && !Array.isArray(current) && depth < 4) {
            if (current.value !== undefined && current.value !== null) {
                current = current.value;
                depth += 1;
                continue;
            }
            break;
        }

        return current;
    }

    function toHandleKey(value) {
        return normalizeLower(value)
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    function parsePossibleJsonArray(value) {
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return [];
            try {
                const parsed = JSON.parse(trimmed);
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                return trimmed.split(',').map(item => item.trim()).filter(Boolean);
            }
        }
        return [];
    }

    function getMetaobjectField(entry, fieldKey) {
        if (!entry) return '';

        if (entry[fieldKey] !== undefined && entry[fieldKey] !== null) {
            const directValue = unwrapMetaobjectValue(entry[fieldKey]);
            if (directValue !== undefined && directValue !== null && directValue !== '') {
                return directValue;
            }
        }

        let result = '';

        if (entry.fields && Array.isArray(entry.fields)) {
            const match = entry.fields.find(field => normalizeLower(field && field.key) === normalizeLower(fieldKey));
            if (match && match.value !== undefined) {
                result = match.value;
            }
        } else {
            const prefixedKey = 'field_' + fieldKey;
            if (entry[prefixedKey] !== undefined && entry[prefixedKey] !== null) {
                result = entry[prefixedKey];
            }
        }

        const unwrapped = unwrapMetaobjectValue(result);
        return unwrapped === undefined || unwrapped === null ? '' : unwrapped;
    }

    function getVariantField(variantMetaobject, fieldKey) {
        if (!variantMetaobject) return '';
        if (variantMetaobject[fieldKey] !== undefined && variantMetaobject[fieldKey] !== null && variantMetaobject[fieldKey] !== '') {
            return variantMetaobject[fieldKey];
        }
        return getMetaobjectField(variantMetaobject, fieldKey);
    }

    function categorizeVariants(variants) {
        const featured = { options: [], originals: [] };
        const quickShip = { options: [], originals: [] };
        const regular = { options: [], originals: [] };

        (variants || []).forEach(value => {
            if (!value || typeof value !== 'string') return;

            if (value.includes('*')) {
                featured.options.push(value.replace(/\*/g, '').trim());
                featured.originals.push(value);
            } else if (value.includes('(QS)')) {
                quickShip.options.push(value.replace(/\(QS\)/g, '').trim());
                quickShip.originals.push(value);
            } else {
                regular.options.push(value);
                regular.originals.push(value);
            }
        });

        return { featured, quickShip, regular };
    }

    function buildMetaobjectIndexes(swatchMetaobjects, upholsteryVariantMetaobjects) {
        const swatchIndex = new Map();
        const upholsteryVariantIndex = new Map();

        (swatchMetaobjects || []).forEach(item => {
            [
                item && item.id,
                item && item.handle,
                getMetaobjectField(item, 'swatch_code'),
                getMetaobjectField(item, 'name'),
                getMetaobjectField(item, 'web_name')
            ].forEach(candidate => {
                const key = normalizeLower(candidate);
                if (key) swatchIndex.set(key, item);
                const handleKey = toHandleKey(candidate);
                if (handleKey) swatchIndex.set(handleKey, item);
                const compactKey = normalizeOptionKey(candidate);
                if (compactKey) swatchIndex.set(compactKey, item);
            });
        });

        (upholsteryVariantMetaobjects || []).forEach(item => {
            [
                item && item.id,
                item && item.handle,
                getVariantField(item, 'variant_name'),
                getVariantField(item, 'display_name')
            ].forEach(candidate => {
                const key = normalizeLower(candidate);
                if (key) upholsteryVariantIndex.set(key, item);
                const handleKey = toHandleKey(candidate);
                if (handleKey) upholsteryVariantIndex.set(handleKey, item);
                const compactKey = normalizeOptionKey(candidate);
                if (compactKey) upholsteryVariantIndex.set(compactKey, item);
            });
        });

        return { swatchIndex, upholsteryVariantIndex };
    }

    function findConfigEntryKey(optionTitle, configList) {
        const optionKey = normalizeLower(optionTitle);
        const optionKeyNorm = normalizeOptionKey(optionTitle);

        for (const item of configList) {
            if (!item || typeof item !== 'object') continue;
            const itemKey = Object.keys(item).find(key => {
                return normalizeLower(key) === optionKey || normalizeOptionKey(key) === optionKeyNorm;
            });
            if (itemKey) return itemKey;
        }

        return '';
    }

    function resolveUpholsterySourceConfig(optionTitle, upholsteryTypeRaw, handlePatternRaw) {
        const upholsteryTypeList = Array.isArray(upholsteryTypeRaw) ? upholsteryTypeRaw : [];
        const handlePatternList = Array.isArray(handlePatternRaw) ? handlePatternRaw : [];

        let sourceType = '';
        const upholsteryTypeKey = findConfigEntryKey(optionTitle, upholsteryTypeList);
        if (upholsteryTypeKey) {
            const matchItem = upholsteryTypeList.find(item => item && item[upholsteryTypeKey] !== undefined);
            if (matchItem) {
                sourceType = normalizeLower(matchItem[upholsteryTypeKey]).replace(/["']/g, '');
            }
        }

        let patterns = [];
        const handlePatternKey = findConfigEntryKey(optionTitle, handlePatternList);
        if (handlePatternKey) {
            const item = handlePatternList.find(entry => entry && entry[handlePatternKey] !== undefined);
            if (item) {
                const rawValue = item[handlePatternKey];
                patterns = parsePossibleJsonArray(rawValue);
                if (patterns.length === 0 && typeof rawValue === 'string') {
                    patterns = rawValue.split(',').map(v => v.trim()).filter(Boolean);
                }
            }
        }

        return { sourceType: sourceType, patterns: patterns };
    }

    function mapSwatchEntry(ref, swatchIndex) {
        if (ref && typeof ref === 'object') {
            const refName = normalizeString(ref.name || ref.Swatch_Name || ref.swatch_name);
            const refWebName = normalizeString(ref.web_name || ref.Web_Name || refName);
            const refUrl = normalizeString(ref.url || ref.Link_Handle || ref.link_handle);
            if (refWebName && refUrl) {
                return {
                    Swatch_Name: refName || refWebName,
                    Web_Name: refWebName,
                    Link_Handle: refUrl
                };
            }

            const nestedSwatch = swatchIndex.get(normalizeLower(ref.handle)) ||
                swatchIndex.get(normalizeLower(ref.id));
            if (nestedSwatch) {
                return mapSwatchEntry(nestedSwatch, swatchIndex);
            }
        }

        if (typeof ref === 'string' && /^(https?:)?\/\//.test(ref)) {
            const directName = ref.split('/').pop() || ref;
            return {
                Swatch_Name: directName,
                Web_Name: directName,
                Link_Handle: ref
            };
        }

        const swatchMetaobject = swatchIndex.get(normalizeLower(ref)) ||
            swatchIndex.get(toHandleKey(ref));
        if (!swatchMetaobject) return null;

        const swatchName = normalizeString(getMetaobjectField(swatchMetaobject, 'name'));
        const swatchWebName = normalizeString(getMetaobjectField(swatchMetaobject, 'web_name')) || swatchName;
        const swatchUrl = normalizeString(getMetaobjectField(swatchMetaobject, 'url'));
        if (!swatchWebName || !swatchUrl) return null;

        return {
            Swatch_Name: swatchName || swatchWebName,
            Web_Name: swatchWebName,
            Link_Handle: swatchUrl
        };
    }

    function buildFabricFromVariantMetaobject(variantMetaobject, swatchIndex) {
        if (!variantMetaobject) return null;

        const variantName = normalizeString(getVariantField(variantMetaobject, 'variant_name'));
        if (!variantName) return null;

        const displayName = normalizeString(getVariantField(variantMetaobject, 'display_name')) || variantName;
        const jsonSwatchRefs = parsePossibleJsonArray(getVariantField(variantMetaobject, 'jsonswatch'));
        const swatchRefs = parsePossibleJsonArray(getVariantField(variantMetaobject, 'swatches'));
        const refsToMap = jsonSwatchRefs.length > 0 ? jsonSwatchRefs : swatchRefs;

        const swatches = refsToMap.map(ref => mapSwatchEntry(ref, swatchIndex)).filter(Boolean);

        if (swatches.length === 0) return null;

        return {
            Upholstery_Variant: variantName,
            Display_Name: displayName,
            Composition: normalizeString(getVariantField(variantMetaobject, 'composition')),
            Durability: normalizeString(getVariantField(variantMetaobject, 'durability')),
            Designer: normalizeString(getVariantField(variantMetaobject, 'designer')),
            Notes: normalizeString(getVariantField(variantMetaobject, 'notes')),
            optionValue: normalizeString(variantMetaobject.option_value),
            Swatches: swatches
        };
    }

    function buildFabricFromExpandedVariantRecord(record, swatchIndex) {
        if (!record || typeof record !== 'object') return null;

        const variantName = normalizeString(record.variant_name);
        if (!variantName) return null;

        const displayName = normalizeString(record.display_name) || variantName;
        const jsonSwatchRefs = parsePossibleJsonArray(record.jsonswatch);
        const swatchRefs = parsePossibleJsonArray(record.swatches);
        const refsToMap = jsonSwatchRefs.length > 0 ? jsonSwatchRefs : swatchRefs;
        const swatches = refsToMap.map(ref => mapSwatchEntry(ref, swatchIndex)).filter(Boolean);

        if (swatches.length === 0) return null;

        return {
            Upholstery_Variant: variantName,
            Display_Name: displayName,
            Composition: normalizeString(record.composition),
            Durability: normalizeString(record.durability),
            Designer: normalizeString(record.designer),
            Notes: normalizeString(record.notes),
            optionValue: normalizeString(record.option_value),
            Swatches: swatches
        };
    }

    function buildMetaobjectDataForGroupVariants(configData, productSetMetaobjects, indexes, productHandle, productVendor, optionValues) {
        const groupData = [];
        const groups = {};
        const uniqueVariants = new Set();
        const productSetList = Array.isArray(productSetMetaobjects) ? productSetMetaobjects : [];
        const vendorHandle = toHandleKey(productVendor);
        const categorized = categorizeVariants(optionValues || []);
        const regularOptions = (categorized.regular && categorized.regular.options) || [];
        const configuredPatterns = (configData.patterns || []).map(v => normalizeString(v)).filter(Boolean);
        const patternTokens = configuredPatterns.length > 0 ? configuredPatterns : ['all-products'];

        regularOptions.forEach(optionValue => {
            const optionLabel = normalizeString(optionValue);
            if (!optionLabel) return;
            const optionHandle = toHandleKey(optionLabel);
            if (!optionHandle) return;

            const candidateHandles = patternTokens
                .map(pattern => toHandleKey(pattern))
                .filter(Boolean)
                .map(patternHandle => vendorHandle + '-' + patternHandle + '-' + optionHandle);

            if (candidateHandles.length === 0) return;

            const matchedProductSet = productSetList.find(item => {
                const itemHandle = normalizeLower(item && item.handle);
                const itemSetHandle = normalizeLower(getMetaobjectField(item, 'product_set_handle'));
                const itemCandidates = [itemHandle, itemSetHandle].filter(Boolean);
                return itemCandidates.some(candidate => candidate && candidateHandles.includes(candidate));
            });

            if (!matchedProductSet) return;

            const variantRefs = parsePossibleJsonArray(getMetaobjectField(matchedProductSet, 'variants'));
            const includes = [];

            variantRefs.forEach(ref => {
                let fabric = null;
                if (ref && typeof ref === 'object' && ref.variant_name) {
                    fabric = buildFabricFromExpandedVariantRecord(ref, indexes.swatchIndex) ||
                        buildFabricFromVariantMetaobject(ref, indexes.swatchIndex);
                } else {
                    const variantMetaobject =
                        indexes.upholsteryVariantIndex.get(normalizeLower(ref)) ||
                        indexes.upholsteryVariantIndex.get(toHandleKey(ref));
                    fabric = buildFabricFromVariantMetaobject(variantMetaobject, indexes.swatchIndex);
                }

                if (!fabric) return;

                includes.push(fabric.Upholstery_Variant);
                if (!uniqueVariants.has(fabric.Upholstery_Variant.toLowerCase())) {
                    uniqueVariants.add(fabric.Upholstery_Variant.toLowerCase());
                    groupData.push(fabric);
                }
            });

            if (includes.length > 0) {
                groups[optionLabel] = { includes: includes };
            }
        });

        if (Object.keys(groups).length > 0) {
            return {
                groupData: groupData,
                categoryData: {
                    [productVendor]: {
                        products: {
                            all_products: { groups: groups }
                        }
                    }
                }
            };
        }

        productSetList.forEach(item => {
            const rawPattern = normalizeString(getMetaobjectField(item, 'handle_pattern'));
            const groupName = normalizeString(getMetaobjectField(item, 'group_name'));
            if (!rawPattern || !groupName) return;

            const patternList = rawPattern.split(',').map(v => normalizeString(v)).filter(Boolean);
            const fromMetafieldPatterns = (configData.patterns || []).map(v => normalizeString(v)).filter(Boolean);
            const patternsToMatch = fromMetafieldPatterns.length > 0 ? fromMetafieldPatterns : patternList;
            const isMatch = patternsToMatch.some(pattern => pattern && productHandle.includes(pattern));
            if (!isMatch) return;

            const variantRefs = parsePossibleJsonArray(getMetaobjectField(item, 'variants'));
            const includes = [];

            variantRefs.forEach(ref => {
                let fabric = null;
                if (ref && typeof ref === 'object' && ref.variant_name) {
                    fabric = buildFabricFromExpandedVariantRecord(ref, indexes.swatchIndex) ||
                        buildFabricFromVariantMetaobject(ref, indexes.swatchIndex);
                } else {
                    const variantMetaobject =
                        indexes.upholsteryVariantIndex.get(normalizeLower(ref)) ||
                        indexes.upholsteryVariantIndex.get(toHandleKey(ref));
                    fabric = buildFabricFromVariantMetaobject(variantMetaobject, indexes.swatchIndex);
                }
                if (!fabric) return;

                includes.push(fabric.Upholstery_Variant);
                if (!uniqueVariants.has(fabric.Upholstery_Variant.toLowerCase())) {
                    uniqueVariants.add(fabric.Upholstery_Variant.toLowerCase());
                    groupData.push(fabric);
                }
            });

            if (includes.length > 0) {
                groups[groupName] = { includes: includes };
            }
        });

        return {
            groupData: groupData,
            categoryData: groupData.length > 0 ? {
                [productVendor]: {
                    products: {
                        all_products: { groups: groups }
                    }
                }
            } : null
        };
    }

    function normalizeVariantMatchKey(value) {
        return normalizeOptionKey(
            normalizeString(value)
                .replace(/\*/g, '')
                .replace(/\(qs\)/gi, '')
        );
    }

    function fabricMatchesProductOption(fabric, productOptionValues) {
        const fabricKeys = [
            fabric.optionValue,
            fabric.Upholstery_Variant,
            fabric.Display_Name
        ].filter(Boolean).map(normalizeVariantMatchKey);

        return (productOptionValues || []).some(optionValue => {
            const optionKey = normalizeVariantMatchKey(optionValue);
            return optionKey && fabricKeys.includes(optionKey);
        });
    }

    function filterGroupDataToProductOptions(groupData, productOptionValues) {
        if (!Array.isArray(groupData)) return [];
        return groupData.filter(fabric => fabricMatchesProductOption(fabric, productOptionValues));
    }

    function optionValueMatchesVariant(optionValue, variantMetaobject) {
        if (!optionValue || !variantMetaobject) return false;

        const valueLower = normalizeLower(optionValue);
        const valueNorm = normalizeOptionKey(optionValue);
        const valueHandle = toHandleKey(optionValue);

        const candidates = [
            getVariantField(variantMetaobject, 'variant_name'),
            getVariantField(variantMetaobject, 'display_name'),
            variantMetaobject.handle
        ].filter(Boolean);

        return candidates.some(candidate => {
            const candidateLower = normalizeLower(candidate);
            const candidateNorm = normalizeOptionKey(candidate);
            const candidateHandle = toHandleKey(candidate);

            return valueLower === candidateLower ||
                valueNorm === candidateNorm ||
                valueHandle === candidateHandle ||
                normalizeVariantMatchKey(optionValue) === normalizeVariantMatchKey(candidate);
        });
    }

    function findUpholsteryVariantMetaobject(indexes, value, allMetaobjects) {
        const lookupKeys = [
            normalizeLower(value),
            toHandleKey(value),
            normalizeOptionKey(value)
        ].filter(Boolean);

        for (let i = 0; i < lookupKeys.length; i++) {
            const match = indexes.upholsteryVariantIndex.get(lookupKeys[i]);
            if (match) return match;
        }

        let fallbackMatch = null;
        (allMetaobjects || []).some(item => {
            if (optionValueMatchesVariant(value, item)) {
                fallbackMatch = item;
                return true;
            }
            return false;
        });

        return fallbackMatch;
    }

    function buildMetaobjectDataForUpholsteryVariants(indexes, optionValues, expandedVariants, upholsteryVariantMetaobjects, productVendor) {
        const groupData = [];
        const seen = new Set();
        const expandedList = Array.isArray(expandedVariants) ? expandedVariants : [];
        const vendorHandle = toHandleKey(productVendor);

        expandedList.forEach(record => {
            const fabric = buildFabricFromExpandedVariantRecord(record, indexes.swatchIndex);
            if (!fabric) return;
            const key = fabric.Upholstery_Variant.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            groupData.push(fabric);
        });

        const processableValues = (optionValues || []).filter(value => {
            if (!value || typeof value !== 'string') return false;
            const handleized = value.toLowerCase().replace(/\s+/g, '-');
            return value !== '_COM' &&
                value !== 'Optional' &&
                !handleized.includes('-qs');
        });

        processableValues.forEach(value => {
            const normalizedValue = normalizeLower(value);
            const handleValue = toHandleKey(value);
            const composedHandle = vendorHandle && handleValue ? vendorHandle + '-' + handleValue : '';
            const strippedKey = normalizeVariantMatchKey(value);

            const variantMetaobject =
                indexes.upholsteryVariantIndex.get(normalizedValue) ||
                indexes.upholsteryVariantIndex.get(handleValue) ||
                (composedHandle ? indexes.upholsteryVariantIndex.get(composedHandle) : null) ||
                indexes.upholsteryVariantIndex.get(strippedKey) ||
                (vendorHandle && strippedKey ? indexes.upholsteryVariantIndex.get(vendorHandle + '-' + strippedKey) : null);

            const fabric = buildFabricFromVariantMetaobject(variantMetaobject, indexes.swatchIndex);
            if (!fabric) return;
            const key = fabric.Upholstery_Variant.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            fabric.optionValue = value;
            groupData.push(fabric);
        });

        return groupData;
    }

    function findSwatchMetaobjectByOption(optionValue, swatchIndex) {
        const normalized = normalizeLower(optionValue)
            .replace(/\*/g, '')
            .replace(/\(qs\)/g, '')
            .trim();
        if (!normalized) return null;

        let match = swatchIndex.get(normalized) || swatchIndex.get(toHandleKey(optionValue)) || swatchIndex.get(normalizeVariantMatchKey(optionValue));
        if (match) return match;

        const valueNorm = normalizeVariantMatchKey(optionValue);
        if (!valueNorm) return null;

        swatchIndex.forEach(item => {
            if (match) return;
            const candidates = [
                getMetaobjectField(item, 'name'),
                getMetaobjectField(item, 'web_name'),
                item && item.handle
            ].filter(Boolean);

            match = candidates.find(candidate => normalizeVariantMatchKey(candidate) === valueNorm) || null;
        });

        return match;
    }

    function buildMetaobjectDataForSwatchMode(indexes, optionValues) {
        const groupData = [];
        const seen = new Set();

        (optionValues || []).forEach(value => {
            if (!value || typeof value !== 'string') return;
            const handleized = value.toLowerCase().replace(/\s+/g, '-');
            if (value === '_COM' || value === 'Optional' || handleized.includes('-qs')) return;

            const swatchMetaobject = findSwatchMetaobjectByOption(value, indexes.swatchIndex);
            if (!swatchMetaobject) return;

            const swatchUrl = normalizeString(getMetaobjectField(swatchMetaobject, 'url'));
            const swatchWebName = normalizeString(getMetaobjectField(swatchMetaobject, 'web_name')) ||
                normalizeString(getMetaobjectField(swatchMetaobject, 'name')) ||
                value;
            if (!swatchUrl) return;

            const key = normalizeLower(value);
            if (seen.has(key)) return;
            seen.add(key);

            groupData.push({
                Upholstery_Variant: value,
                Display_Name: swatchWebName,
                Swatches: [{
                    Swatch_Name: swatchWebName,
                    Web_Name: swatchWebName,
                    Link_Handle: swatchUrl
                }]
            });
        });

        return groupData;
    }

    function resolveMetaobjectFabricData(jsonLoader) {
        if (!jsonLoader) {
            return { groupData: null, categoryData: null, useFallback: true };
        }

        const variants = safeJsonParse(jsonLoader.dataset.productVariants, []);
        const productHandle = jsonLoader.dataset.productHandle;
        const productVendor = jsonLoader.dataset.productVendor;
        const optionTitle = jsonLoader.dataset.optionTitle || jsonLoader.dataset.optionName;
        const upholsteryType = parseUpholsteryConfigList(jsonLoader.dataset.upholsteryType);
        const handlePattern = parseUpholsteryConfigList(jsonLoader.dataset.handlePattern);
        const sourceConfig = resolveUpholsterySourceConfig(optionTitle, upholsteryType, handlePattern);

        if (!sourceConfig.sourceType || sourceConfig.sourceType === 'none') {
            return { groupData: null, categoryData: null, useFallback: true };
        }

        const swatchMetaobjects = safeJsonParse(jsonLoader.dataset.swatchMetaobjects, []);
        const upholsteryVariantMetaobjects = safeJsonParse(jsonLoader.dataset.upholsteryVariantMetaobjects, []);
        const productSetMetaobjects = safeJsonParse(jsonLoader.dataset.productSetMetaobjects, []);
        const expandedUpholsteryVariants = parseUpholsteryConfigList(jsonLoader.dataset.expandedUpholsteryVariants);
        const indexes = buildMetaobjectIndexes(swatchMetaobjects, upholsteryVariantMetaobjects);

        let groupData = null;
        let categoryData = null;

        if (sourceConfig.sourceType === 'group-variants') {
            const metaobjectData = buildMetaobjectDataForGroupVariants(
                sourceConfig,
                productSetMetaobjects,
                indexes,
                productHandle,
                productVendor,
                variants
            );
            groupData = metaobjectData.groupData;
            categoryData = metaobjectData.categoryData;
        } else if (sourceConfig.sourceType === 'upholstery-variants') {
            groupData = buildMetaobjectDataForUpholsteryVariants(
                indexes,
                variants,
                expandedUpholsteryVariants,
                upholsteryVariantMetaobjects,
                productVendor
            );
            categoryData = null;

            if (!groupData || groupData.length === 0) {
                const groupFallback = buildMetaobjectDataForGroupVariants(
                    { patterns: sourceConfig.patterns.length > 0 ? sourceConfig.patterns : [productHandle] },
                    productSetMetaobjects,
                    indexes,
                    productHandle,
                    productVendor,
                    variants
                );
                groupData = groupFallback.groupData;
                categoryData = groupFallback.categoryData;
            }

            if (!groupData || groupData.length === 0) {
                groupData = buildMetaobjectDataForSwatchMode(indexes, variants);
            }
        } else if (sourceConfig.sourceType === 'swatch' || sourceConfig.sourceType === 'swatches') {
            groupData = buildMetaobjectDataForSwatchMode(indexes, variants);
            categoryData = null;
        } else {
            return { groupData: null, categoryData: null, useFallback: true };
        }

        if (!groupData || groupData.length === 0) {
            return { groupData: null, categoryData: null, useFallback: true };
        }

        if (sourceConfig.sourceType === 'upholstery-variants') {
            groupData = filterGroupDataToProductOptions(groupData, variants);
            if (!groupData.length) {
                return { groupData: null, categoryData: null, useFallback: true };
            }
        }

        return { groupData: groupData, categoryData: categoryData, useFallback: false };
    }

    global.UpholsteryMetaobject = {
        safeJsonParse: safeJsonParse,
        resolveMetaobjectFabricData: resolveMetaobjectFabricData
    };
})(typeof window !== 'undefined' ? window : this);
